import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box } from '@mui/material';
import AnalysisSidebar, { SIDEBAR_EXPANDED_W, SIDEBAR_COLLAPSED_W } from './components/layout/AnalysisSidebar';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChartProvider } from './contexts/ChartContext';
import { AppMode, THEME_PALETTE_COLORS, ThemePaletteName, UserPreferencesProvider, useUserPreferences } from './contexts/UserPreferencesContext';
import Navbar, { WEBSITE_NAVBAR_OFFSET } from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import StudioLandingPage from './pages/StudioLandingPage';
import ChartCalculation from './pages/ChartCalculation';
import ChartDisplay from './pages/ChartDisplay';
import CelebrityChartPractice from './pages/CelebrityChartPractice';
import KPDisplay from './pages/KPDisplay';
import Dashboard from './pages/Dashboard';
import ClientManagement from './pages/ClientManagement';
import Planner from './pages/Planner';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import SharedChart from './pages/SharedChart';
import RulingPlanets from './pages/RulingPlanets';
import CurrentTransit from './pages/CurrentTransit';
import GocharAlerts from './pages/GocharAlerts';
import AdminPanel from './pages/AdminPanel';
import UpgradePage from './pages/UpgradePage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import DivisionalCharts from './pages/DivisionalCharts';
import WesternAstrology from './pages/WesternAstrology';
import AdvancedTools from './pages/AdvancedTools';
import BirthTimeRectification from './pages/BirthTimeRectification';
import BNNAnalysis from './pages/BNNAnalysis';
import JaiminiAnalysis from './pages/JaiminiAnalysis';
import TithiPravesh from './pages/TithiPravesh';
import VedicNumerology from './pages/VedicNumerology';
import LoShuNumerology from './pages/LoShuNumerology';
import Panchang from './pages/Panchang';
import PanchPakshi from './pages/PanchPakshi';
import TajikaVarshphal from './pages/TajikaVarshphal';
import Muhurat from './pages/Muhurat';
import ChildBirthMuhurat from './pages/ChildBirthMuhurat';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PricingPage from './pages/PricingPage';
import Contact from './pages/Contact';
import PaymentThankYou from './pages/PaymentThankYou';
import ChakraAnalysis from './pages/ChakraAnalysis';
import LalKitabGuide from './pages/LalKitabGuide';
import Faladesh from './pages/Faladesh';
import ChartRemedies from './pages/ChartRemedies';
import WatchRecommendation from './pages/WatchRecommendation';
import TermsOfService from './pages/TermsOfService';
import Disclaimer from './pages/Disclaimer';
import DeleteAccount from './pages/DeleteAccount';
import AboutUs from './pages/AboutUs';
import KundlistarPage from './pages/KundlistarPage';
import KundlistarVsKundliSoft from './pages/KundlistarVsKundliSoft';
import KpAstrologySoftwarePage from './pages/KpAstrologySoftwarePage';
import NadiAstrologySoftwarePage from './pages/NadiAstrologySoftwarePage';
import SeoLandingPage, { seoLandingPagePaths } from './pages/SeoLandingPages';
import IosModuleLandingPage, { iosModuleLandingPagePaths } from './pages/IosModuleLandingPages';
import { ChangelogPage, ComparisonPage, FAQStylePage, KundlistarFeaturesPage } from './pages/AeoAuthorityPages';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import BlogAdminDashboard from './pages/BlogAdminDashboard';
import InstallIPhone from './pages/InstallIPhone';
import CampaignLandingPage from './pages/CampaignLandingPage';
import RouteSeo from './components/seo/RouteSeo';
import { normalizeBrandTitle } from './utils/brandTitle';
import './App.css';

// Create custom theme for Nadi astrology app
const createAppTheme = (mode: AppMode, themePalette: ThemePaletteName = 'green') => {
  const palette = THEME_PALETTE_COLORS[themePalette] || THEME_PALETTE_COLORS.green;
  const muiMode = mode === 'light' ? 'light' : 'dark';
  const isDark = mode !== 'light';
  const isNight = mode === 'night';
  const brand = palette.main || '#0F6E56';
  const brandPressed = palette.dark || '#0A5240';
  const brandSoft = themePalette === 'green' ? '#E1F5EE' : `${brand}14`;
  const brandOnSoft = themePalette === 'green' ? '#085041' : brandPressed;
  const neutrals = {
    page: isNight ? '#050805' : isDark ? '#07130D' : '#F6F7F6',
    card: isNight ? '#0D1A11' : isDark ? '#0F1F16' : '#FFFFFF',
    border: isNight ? '#203327' : isDark ? '#26352C' : '#E6E8E6',
    borderStrong: isNight ? '#31503B' : isDark ? '#3A4A40' : '#D2D6D3',
    text: isNight ? '#F2FFF4' : isDark ? '#E8EEE9' : '#1A1D1B',
    secondary: isNight ? '#BCD3C1' : isDark ? '#A8B7AD' : '#5B635E',
    tertiary: isNight ? '#89A38F' : isDark ? '#748079' : '#9AA09B',
    danger: isDark ? '#F87171' : '#C0392B',
  };

  return createTheme({
  palette: {
    mode: muiMode,
    primary: {
      main: brand,
      light: brandSoft,
      dark: brandPressed,
      contrastText: '#ffffff'
    },
    secondary: {
      main: brandOnSoft,
      light: brandSoft,
      dark: brandPressed,
      contrastText: isDark ? '#07130D' : '#ffffff'
    },
    background: {
      default: neutrals.page,
      paper: neutrals.card
    },
    text: {
      primary: neutrals.text,
      secondary: neutrals.secondary
    },
    divider: neutrals.border,
    error: {
      main: neutrals.danger,
    },
  },
  typography: {
    fontFamily: '"Roboto", "Open Sans", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      color: neutrals.text
    },
    h2: {
      fontWeight: 600,
      color: neutrals.text
    },
    h3: {
      fontWeight: 600,
      color: neutrals.text
    },
    h4: { fontWeight: 600, color: neutrals.text },
    h5: { fontWeight: 600, color: neutrals.text },
    h6: { fontWeight: 600, color: neutrals.text },
    body1: { lineHeight: 1.6, fontWeight: 400 },
    body2: { fontWeight: 400 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          minHeight: 44,
          padding: '10px 20px',
          fontWeight: 600,
          boxShadow: 'none',
        },
        containedPrimary: {
          backgroundColor: brand,
          '&:hover': {
            backgroundColor: brandPressed,
            boxShadow: 'none',
          },
        },
        outlined: {
          borderColor: neutrals.borderStrong,
          color: neutrals.text,
        },
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          border: `1px solid ${neutrals.border}`,
          backgroundImage: 'none',
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: neutrals.border,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: neutrals.card,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: neutrals.borderStrong,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: neutrals.borderStrong,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: brand,
            borderWidth: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: neutrals.secondary,
          fontWeight: 500,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: neutrals.borderStrong,
          color: neutrals.secondary,
          fontWeight: 600,
          textTransform: 'none',
          '&.Mui-selected': {
            backgroundColor: brandSoft,
            color: brandOnSoft,
            '&:hover': {
              backgroundColor: brandSoft,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
        colorPrimary: {
          backgroundColor: brandSoft,
          color: brandOnSoft,
        },
      },
    }
  }
  });
};

const AppContent: React.FC = () => {
  const { state } = useAuth();
  const isBootstrappingSession = state.isLoading && !!state.token && !state.isAuthenticated;

  React.useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('lang')) return;

    url.searchParams.delete('lang');
    const cleanPath = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, document.title, cleanPath || '/');
  }, []);

  if (isBootstrappingSession) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={50} />
        <div>Loading...</div>
      </Box>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <BrandTitleGuard />
      <div className="App">
        <RouteSeo />
        <Navbar />
        <AppLayout>
        <main style={{ minHeight: 'calc(100vh - 140px)' }}>
          <Routes>
            <Route path="/" element={<StudioLandingPage />} />
            <Route path="/start" element={<CampaignLandingPage />} />
            <Route path="/studio" element={<StudioLandingPage />} />
            <Route path="/studio/features" element={<StudioLandingPage />} />
            <Route path="/studio/systems" element={<StudioLandingPage />} />
            <Route path="/studio/courses" element={<Navigate to="/studio" replace />} />
            <Route path="/studio/for-astrologers" element={<Navigate to="/studio" replace />} />
            <Route path="/studio/for-teachers" element={<Navigate to="/studio" replace />} />
            <Route path="/studio/for-institutes" element={<Navigate to="/studio" replace />} />
            <Route path="/studio/book-demo" element={<Contact />} />
            <Route path="/studio/login" element={<Login />} />
            <Route path="/studio/signup" element={<Register />} />
            <Route path="/studio/invite/:token" element={<Login />} />
            <Route path="/studio/pricing" element={<PricingPage />} />
            <Route path="/studio/privacy" element={<PrivacyPolicy />} />
            <Route path="/studio/terms" element={<TermsOfService />} />
            <Route path="/studio/support" element={<Contact />} />
            <Route path="/kundlistar-studio" element={<KundlistarPage />} />
            <Route path="/kundlistar" element={<Navigate to="/kundlistar-studio" replace />} />
            <Route path="/kundlistar-studio-vs-kundlisoft" element={<KundlistarVsKundliSoft />} />
            <Route path="/kundlistar-vs-kundlisoft" element={<Navigate to="/kundlistar-studio-vs-kundlisoft" replace />} />
            <Route path="/features" element={<KundlistarFeaturesPage />} />
            <Route path="/kp-astrology-software" element={<KpAstrologySoftwarePage />} />
            <Route path="/nadi-astrology-software" element={<NadiAstrologySoftwarePage />} />
            {seoLandingPagePaths.map((path) => (
              <Route key={path} path={path} element={<SeoLandingPage path={path} />} />
            ))}
            {iosModuleLandingPagePaths.map((path) => (
              <Route key={path} path={path} element={<IosModuleLandingPage path={path} />} />
            ))}
            <Route path="/best-kp-astrology-software" element={<FAQStylePage page="/best-kp-astrology-software" />} />
            <Route path="/vedic-kp-nadi-numerology-app" element={<FAQStylePage page="/vedic-kp-nadi-numerology-app" />} />
            <Route path="/south-indian-kundli-chart-online" element={<FAQStylePage page="/south-indian-kundli-chart-online" />} />
            <Route path="/best-astrology-app-professional-astrologers-india" element={<FAQStylePage page="/best-astrology-app-professional-astrologers-india" />} />
            <Route path="/kundlistar-studio-vs-astrosage" element={<ComparisonPage page="/kundlistar-studio-vs-astrosage" />} />
            <Route path="/kundlistar-studio-vs-jagannatha-hora" element={<ComparisonPage page="/kundlistar-studio-vs-jagannatha-hora" />} />
            <Route path="/kundlistar-studio-vs-parashara-light" element={<ComparisonPage page="/kundlistar-studio-vs-parashara-light" />} />
            <Route path="/kundlistar-vs-astrosage" element={<Navigate to="/kundlistar-studio-vs-astrosage" replace />} />
            <Route path="/kundlistar-vs-jagannatha-hora" element={<Navigate to="/kundlistar-studio-vs-jagannatha-hora" replace />} />
            <Route path="/kundlistar-vs-parashara-light" element={<Navigate to="/kundlistar-studio-vs-parashara-light" replace />} />
            <Route path="/best-astrology-software-indian-astrologers" element={<ComparisonPage page="/best-astrology-software-indian-astrologers" />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/calculate" element={<ChartCalculation />} />
            <Route path="/horary" element={<ChartCalculation />} />
            <Route path="/practice-charts" element={<CelebrityChartPractice />} />
            <Route path="/chart-database" element={<CelebrityChartPractice />} />
            <Route path="/ruling-planets" element={<RulingPlanets />} />
            <Route path="/vedic-numerology" element={<VedicNumerology />} />
            <Route path="/lo-shu-grid-numerology" element={<LoShuNumerology />} />
            <Route path="/tarot" element={<Navigate to="/studio" replace />} />
            <Route path="/panchang" element={<Panchang />} />
            <Route path="/choghadiya" element={<Navigate to="/studio" replace />} />
            <Route path="/muhurat" element={<Muhurat />} />
            <Route path="/birth-muhurat" element={<ChildBirthMuhurat />} />
            <Route path="/child-birth-muhurat" element={<Navigate to="/birth-muhurat" replace />} />
            <Route path="/panch-pakshi" element={<PanchPakshi />} />
            <Route
              path="/chart/:id/astro-logo"
              element={
                <RequireAuth>
                  <Navigate to="/dashboard" replace />
                </RequireAuth>
              }
            />
            <Route path="/daily-horoscope" element={<Navigate to="/studio" replace />} />
            <Route path="/daily-horoscope/:sign" element={<Navigate to="/studio" replace />} />
            <Route path="/matching" element={<Navigate to="/dashboard" replace />} />
            <Route path="/vastu" element={<Navigate to="/dashboard" replace />} />
            <Route path="/watch-recommendation" element={<WatchRecommendation />} />
            <Route path="/transit" element={<GocharAlerts />} />
            <Route path="/gochar-alerts" element={<GocharAlerts />} />
            <Route path="/current-transit" element={<CurrentTransit />} />
            {/* Old combined page for backward compatibility */}
            <Route path="/chart/:id" element={<ChartDisplay />} />
            <Route path="/:id" element={<ChartDisplay />} />
            {/* New separated analysis pages */}
            <Route path="/chart/:id/nadi" element={<ChartDisplay />} />
            <Route path="/chart/:id/kp" element={<KPDisplay />} />
            <Route path="/chart/:id/divisional" element={<DivisionalCharts />} />
            <Route path="/chart/:id/faladesh" element={<RequireAuth><Faladesh /></RequireAuth>} />
            <Route path="/chart/:id/remedies" element={<RequireAuth><ChartRemedies /></RequireAuth>} />
            <Route path="/chart/:id/western" element={<WesternAstrology />} />
            <Route path="/chart/:id/advanced-tools" element={<RequireAuth><AdvancedTools /></RequireAuth>} />
            <Route path="/advanced-tools" element={<RequireAuth><AdvancedTools /></RequireAuth>} />
            <Route
              path="/chart/:id/gemstones"
              element={
                <RequireAuth>
                  <Navigate to="/dashboard" replace />
                </RequireAuth>
              }
            />
            <Route path="/chart/:id/jaimini" element={<JaiminiAnalysis />} />
            <Route path="/chart/:id/tithi-pravesh" element={<TithiPravesh />} />
            <Route path="/chart/:id/transit" element={<GocharAlerts />} />
            <Route
              path="/chart/:id/chakra"
              element={
                <RequireAuth>
                  <ChakraAnalysis />
                </RequireAuth>
              }
            />
            <Route
              path="/chart/:id/varshphal"
              element={
                <RequireAuth>
                  <TajikaVarshphal />
                </RequireAuth>
              }
            />
            <Route
              path="/varshphal"
              element={
                <RequireAuth>
                  <TajikaVarshphal />
                </RequireAuth>
              }
            />
            <Route
              path="/chart/:id/lal-kitab"
              element={
                <RequireAuth>
                  <LalKitabGuide />
                </RequireAuth>
              }
            />
            <Route
              path="/lal-kitab"
              element={
                <RequireAuth>
                  <LalKitabGuide />
                </RequireAuth>
              }
            />
            <Route path="/chart/:id/rectification" element={<BirthTimeRectification />} />
            <Route path="/chart/:id/bnn" element={<BNNAnalysis />} />
            <Route
              path="/chart/:id/vastu"
              element={
                <RequireAuth>
                  <Navigate to="/dashboard" replace />
                </RequireAuth>
              }
            />
            <Route path="/chart/:id/panch-pakshi" element={<PanchPakshi />} />
            <Route
              path="/chart/:id/muhurat"
              element={
                <RequireAuth>
                  <Muhurat />
                </RequireAuth>
              }
            />
            <Route path="/share/:shareCode" element={<SharedChart />} />
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/workspace/charts"
              element={
                <RequireAuth>
                  <Navigate to="/dashboard" replace />
                </RequireAuth>
              }
            />
            <Route
              path="/workspace/:module"
              element={
                <RequireAuth>
                  <Navigate to="/cms" replace />
                </RequireAuth>
              }
            />
            <Route
              path="/cms"
              element={<ClientManagement />}
            />
            <Route
              path="/cms/clients/:clientId"
              element={<ClientManagement />}
            />
            <Route
              path="/cms/planner"
              element={<Navigate to="/planner" replace />}
            />
            <Route
              path="/cms/:section"
              element={<ClientManagement />}
            />
            <Route path="/planner" element={<Planner />} />
            <Route path="/predictions" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/upgrade" element={<UpgradePage />} />
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <AdminPanel />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/blog"
              element={
                <RequireAuth>
                  <BlogAdminDashboard />
                </RequireAuth>
              }
            />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/install-iphone" element={<InstallIPhone />} />
            <Route path="/payment-thank-you" element={<PaymentThankYou />} />
            <Route path="/ai-report-addons" element={<Navigate to="/dashboard" replace />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/terms-and-conditions" element={<TermsOfService />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/confirm-deletion/:token" element={<DeleteAccount />} />
            <Route path="/delete-account/confirm/:token" element={<DeleteAccount />} />
          </Routes>
        </main>
        <Footer />
        </AppLayout>
      </div>
    </Router>
  );
};

const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView();
      });
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
};

const BrandTitleGuard: React.FC = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    const normalizeHeadTitles = () => {
      const normalizedDocumentTitle = normalizeBrandTitle(document.title);
      if (normalizedDocumentTitle !== document.title) {
        document.title = normalizedDocumentTitle;
      }

      document.head
        .querySelectorAll<HTMLMetaElement>('meta[property="og:title"], meta[name="twitter:title"]')
        .forEach((meta) => {
          const normalizedContent = normalizeBrandTitle(meta.content);
          if (normalizedContent !== meta.content) meta.content = normalizedContent;
        });
    };

    normalizeHeadTitles();
    const observer = new MutationObserver(normalizeHeadTitles);
    observer.observe(document.head, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['content'],
    });

    return () => observer.disconnect();
  }, [pathname]);

  return null;
};

// Must be rendered inside <Router> so useLocation is available
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { state: authState } = useAuth();
  const [expanded, setExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem('analysisSidebarOpen_v2') !== 'false'; } catch { return true; }
  });

  const chartMatch = location.pathname.match(/^\/chart\/([^/]+)/);
  const chartId    = chartMatch?.[1];
  const showSidebar = authState.isAuthenticated && Boolean(chartId);
  const sidebarW    = expanded ? SIDEBAR_EXPANDED_W : SIDEBAR_COLLAPSED_W;

  const handleToggle = () => {
    setExpanded((v) => {
      const next = !v;
      try { localStorage.setItem('analysisSidebarOpen_v2', String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <>
      {showSidebar && (
        <AnalysisSidebar chartId={chartId!} expanded={expanded} onToggle={handleToggle} />
      )}
      <Box
        sx={{
          ml: showSidebar ? { xs: 0, md: `${sidebarW}px` } : 0,
          pt: WEBSITE_NAVBAR_OFFSET,
          transition: 'margin-left 0.2s ease',
        }}
      >
        {children}
      </Box>
    </>
  );
};

const RequireAuth: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { state } = useAuth();
  const location = useLocation();

  if (!state.isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return children;
};

const App: React.FC = () => {
  return (
    <UserPreferencesProvider>
      <ThemedApp />
    </UserPreferencesProvider>
  );
};

const ThemedApp: React.FC = () => {
  const { mode, themePalette } = useUserPreferences();
  const theme = React.useMemo(() => createAppTheme(mode, themePalette), [mode, themePalette]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <ChartProvider>
          <AppContent />
        </ChartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
